import { Edit, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';

export default function GerenciadorAddons() {
  const [eventos, setEventos] = useState([]);
  const [addons, setAddons] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [error, setError] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [formulario, setFormulario] = useState({
    nome: '',
    descricao: '',
    valor: '',
  });

  useEffect(() => {
    carregar();
  }, []);

  async function carregar() {
    try {
      setCarregando(true);
      const { data } = await api.get('/admin/eventos');
      setEventos(data);

      // Carregar add-ons para cada evento
      const addonsPorEvento = {};
      for (const evento of data) {
        try {
          const res = await api.get(`/admin/eventos/${evento.id}/addons`);
          addonsPorEvento[evento.id] = res.data || [];
        } catch (err) {
          addonsPorEvento[evento.id] = [];
        }
      }
      setAddons(addonsPorEvento);
    } catch (err) {
      setError('Erro ao carregar eventos');
    } finally {
      setCarregando(false);
    }
  }

  async function salvarAddon() {
    if (!formulario.nome || !formulario.descricao || !formulario.valor) {
      alert('Preencha todos os campos');
      return;
    }

    setSalvando(true);
    try {
      const priceInCents = Math.round(parseFloat(formulario.valor) * 100);

      if (editando) {
        // Atualizar
        await api.put(
          `/admin/eventos/${eventoSelecionado}/addons/${editando.id}`,
          {
            name: formulario.nome,
            description: formulario.descricao,
            priceInCents,
          },
        );
      } else {
        // Criar
        await api.post(`/admin/eventos/${eventoSelecionado}/addons`, {
          name: formulario.nome,
          description: formulario.descricao,
          priceInCents,
        });
      }

      setModalAberto(false);
      setFormulario({ nome: '', descricao: '', valor: '' });
      setEditando(null);
      carregar();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar add-on');
    } finally {
      setSalvando(false);
    }
  }

  async function deletarAddon(eventoId, addonId) {
    if (!confirm('Deseja deletar este add-on?')) return;

    try {
      await api.delete(`/admin/eventos/${eventoId}/addons/${addonId}`);
      carregar();
    } catch (err) {
      setError('Erro ao deletar add-on');
    }
  }

  async function toggleAtivo(eventoId, addonId) {
    try {
      await api.patch(`/admin/eventos/${eventoId}/addons/${addonId}/toggle`);
      carregar();
    } catch (err) {
      setError('Erro ao alternar status');
    }
  }

  function abrirModal(evento, addon = null) {
    setEventoSelecionado(evento.id);
    if (addon) {
      setEditando(addon);
      setFormulario({
        nome: addon.name,
        descricao: addon.description,
        valor: (addon.priceInCents / 100).toFixed(2),
      });
    } else {
      setEditando(null);
      setFormulario({ nome: '', descricao: '', valor: '' });
    }
    setModalAberto(true);
  }

  if (carregando)
    return (
      <AdminLayout>
        <div style={{ padding: 64, textAlign: 'center', color: '#94a3b8' }}>
          Carregando...
        </div>
      </AdminLayout>
    );

  const styles = {
    container: { padding: 32 },
    h1: { fontSize: 24, fontWeight: 900, color: '#0f172a', marginBottom: 8 },
    subtitle: { fontSize: 13, color: '#94a3b8', marginBottom: 32 },
    eventCard: {
      background: '#fff',
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      border: '1px solid #f1f5f9',
      boxShadow: '0 1px 4px rgba(0,0,0,.04)',
    },
    eventTitle: {
      fontSize: 16,
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: 12,
    },
    addonsTable: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 13,
    },
    th: {
      textAlign: 'left',
      padding: 12,
      borderBottom: '2px solid #f1f5f9',
      fontWeight: 700,
      color: '#64748b',
      background: '#f8fafc',
    },
    td: {
      padding: 12,
      borderBottom: '1px solid #f1f5f9',
      color: '#475569',
    },
    btn: {
      padding: '8px 16px',
      borderRadius: 8,
      border: 'none',
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 700,
    },
    btnPrimary: {
      background: '#ff2d6c',
      color: '#fff',
      marginBottom: 16,
    },
    btnSecondary: {
      background: '#f8fafc',
      color: '#475569',
      border: '1px solid #e2e8f0',
      marginRight: 8,
    },
    modal: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
    },
    modalContent: {
      background: '#fff',
      borderRadius: 16,
      padding: 28,
      maxWidth: 400,
      width: '100%',
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      marginBottom: 16,
      fontSize: 11,
      fontWeight: 900,
      color: '#94a3b8',
      textTransform: 'uppercase',
    },
    input: {
      padding: '12px 16px',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      fontSize: 14,
      fontFamily: 'inherit',
    },
  };

  return (
    <AdminLayout>
      <div style={styles.container}>
        <h1 style={styles.h1}>Gerenciador de Add-ons</h1>
        <p style={styles.subtitle}>
          Crie e edite valores extras para cada evento
        </p>

        {error && (
          <div
            style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {eventos.map((evento) => (
          <div key={evento.id} style={styles.eventCard}>
            <div style={styles.eventTitle}>{evento.title}</div>

            {(addons[evento.id]?.length || 0) > 0 ? (
              <table style={styles.addonsTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>Nome</th>
                    <th style={styles.th}>Descrição</th>
                    <th style={styles.th}>Valor</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {addons[evento.id].map((addon) => (
                    <tr key={addon.id}>
                      <td style={styles.td}>{addon.name}</td>
                      <td style={styles.td}>{addon.description}</td>
                      <td style={styles.td}>
                        R$ {(addon.priceInCents / 100).toFixed(2)}
                      </td>
                      <td style={styles.td}>
                        <button
                          style={{
                            ...styles.btn,
                            background: addon.active ? '#10b981' : '#cbd5e1',
                            color: '#fff',
                          }}
                          onClick={() => toggleAtivo(evento.id, addon.id)}
                        >
                          {addon.active ? (
                            <Eye size={14} />
                          ) : (
                            <EyeOff size={14} />
                          )}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={{ ...styles.btn, ...styles.btnSecondary }}
                          onClick={() => abrirModal(evento, addon)}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          style={{
                            ...styles.btn,
                            ...styles.btnSecondary,
                            color: '#dc2626',
                          }}
                          onClick={() => deletarAddon(evento.id, addon.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: 12 }}>
                Nenhum add-on configurado
              </p>
            )}

            <button
              style={{ ...styles.btn, ...styles.btnPrimary, marginTop: 16 }}
              onClick={() => abrirModal(evento)}
            >
              <Plus size={14} style={{ marginRight: 4 }} /> Adicionar Add-on
            </button>
          </div>
        ))}
      </div>

      {modalAberto && (
        <div style={styles.modal} onClick={() => setModalAberto(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: '#0f172a',
                marginBottom: 20,
              }}
            >
              {editando ? 'Editar Add-on' : 'Novo Add-on'}
            </h2>

            <label style={styles.label}>
              Nome
              <input
                style={styles.input}
                value={formulario.nome}
                onChange={(e) =>
                  setFormulario({ ...formulario, nome: e.target.value })
                }
                placeholder="ex: Ingresso cinema"
              />
            </label>

            <label style={styles.label}>
              Descrição / Justificativa
              <input
                style={styles.input}
                value={formulario.descricao}
                onChange={(e) =>
                  setFormulario({ ...formulario, descricao: e.target.value })
                }
                placeholder="ex: Sessão exclusiva para cinema"
              />
            </label>

            <label style={styles.label}>
              Valor (R$)
              <input
                style={styles.input}
                type="number"
                step="0.01"
                min="0"
                value={formulario.valor}
                onChange={(e) =>
                  setFormulario({ ...formulario, valor: e.target.value })
                }
                placeholder="25.00"
              />
            </label>

            <div
              style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}
            >
              <button
                style={{ ...styles.btn, ...styles.btnSecondary }}
                onClick={() => setModalAberto(false)}
              >
                Cancelar
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={salvarAddon}
                disabled={salvando}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
